import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, User, Building2, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { contactInfo as mockContactInfo } from '../data/mock';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { getText, staticTexts } from '../utils/translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [contactCards, setContactCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(true);

  const txt = staticTexts.contact;

  // Fetch contact cards from API
  useEffect(() => {
    const fetchContactCards = async () => {
      try {
        const response = await axios.get(`${API}/contact-cards`);
        if (response.data && response.data.length > 0) {
          setContactCards(response.data);
        }
      } catch (error) {
        console.error('Error fetching contact cards:', error);
      } finally {
        setCardsLoading(false);
      }
    };
    fetchContactCards();
  }, []);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(getText(language, txt.form.success.en, txt.form.success.fr, txt.form.success.ar));
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error(getText(language, txt.form.error.en, txt.form.error.fr, txt.form.error.ar));
    } finally {
      setLoading(false);
    }
  };

  const getCardIcon = (cardType) => {
    switch(cardType) {
      case 'person':
      case 'secretary':
        return User;
      case 'address':
        return MapPin;
      case 'sales_point':
        return Building2;
      default:
        return User;
    }
  };

  const getCardTitle = (card) => {
    if (language === 'ar' && card.title_ar) return card.title_ar;
    if (language === 'fr' && card.title_fr) return card.title_fr;
    return card.title_en || card.title_fr;
  };

  // Render contact cards from API
  const renderContactCards = () => {
    if (cardsLoading) {
      return (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      );
    }

    if (contactCards.length > 0) {
      return (
        <div className="space-y-4">
          {contactCards.map((card) => {
            const Icon = getCardIcon(card.card_type);
            return (
              <div key={card.id} className="bg-white rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon className="text-slate-400" size={18} />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-widest text-slate-400 mb-1">
                      {getCardTitle(card)}
                    </p>
                    {card.name && (
                      <p className="font-medium text-slate-800">{card.name}</p>
                    )}
                    {card.phones && card.phones.length > 0 && (
                      <div className="flex flex-wrap gap-3 mt-2">
                        {card.phones.map((phone, index) => (
                          <a 
                            key={index} 
                            href={`tel:${phone}`} 
                            className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1"
                          >
                            <Phone size={12} /> {phone}
                          </a>
                        ))}
                      </div>
                    )}
                    {card.emails && card.emails.length > 0 && (
                      <div className="flex flex-wrap gap-3 mt-1">
                        {card.emails.map((email, index) => (
                          <a 
                            key={index} 
                            href={`mailto:${email}`} 
                            className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1"
                          >
                            <Mail size={12} /> {email}
                          </a>
                        ))}
                      </div>
                    )}
                    {card.address && (
                      <p className="text-sm text-slate-600 mt-2">{card.address}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Fallback to mock data
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="text-slate-400" size={18} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400 mb-1">{t('contact.manager')}</p>
              <p className="font-medium text-slate-800">{mockContactInfo.manager.name}</p>
              <div className="flex flex-wrap gap-3 mt-2">
                {mockContactInfo.manager.phones.map((phone, index) => (
                  <a key={index} href={`tel:${phone}`} className="text-sm text-slate-500 hover:text-slate-800">
                    {phone}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
              <MapPin className="text-slate-400" size={18} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400 mb-1">{t('contact.address')}</p>
              <p className="text-slate-600">{mockContactInfo.mainAddress}</p>
              <div className="mt-3 space-y-1">
                <a href={`tel:${mockContactInfo.mainPhone}`} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800">
                  <Phone size={14} /> {mockContactInfo.mainPhone}
                </a>
                <a href={`mailto:${mockContactInfo.mainEmail}`} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800">
                  <Mail size={14} /> {mockContactInfo.mainEmail}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      {/* Page Header */}
      <section className="pt-20 pb-16 bg-slate-100">
        <div className="container mx-auto px-6">
          <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">
            {getText(language, txt.getInTouch.en, txt.getInTouch.fr, txt.getInTouch.ar)}
          </p>
          <h1 className="text-5xl md:text-6xl font-light text-slate-800">
            {getText(language, txt.title.en, txt.title.fr, txt.title.ar)}
          </h1>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Cards */}
            <div>
              {renderContactCards()}
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8">
              <h2 className="text-2xl font-light text-slate-800 mb-8">
                {getText(language, txt.form.title.en, txt.form.title.fr, txt.form.title.ar)}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm text-slate-600">
                    {getText(language, txt.form.name.en, txt.form.name.fr, txt.form.name.ar)}
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="h-12 border-slate-200 rounded-lg bg-slate-50"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm text-slate-600">
                    {getText(language, txt.form.email.en, txt.form.email.fr, txt.form.email.ar)}
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="h-12 border-slate-200 rounded-lg bg-slate-50"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-sm text-slate-600">
                    {getText(language, txt.form.subject.en, txt.form.subject.fr, txt.form.subject.ar)}
                  </Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="h-12 border-slate-200 rounded-lg bg-slate-50"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-sm text-slate-600">
                    {getText(language, txt.form.message.en, txt.form.message.fr, txt.form.message.ar)}
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="border-slate-200 rounded-lg bg-slate-50"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-slate-800 hover:bg-slate-700 text-white h-12 rounded-full"
                >
                  {loading 
                    ? getText(language, txt.form.sending.en, txt.form.sending.fr, txt.form.sending.ar)
                    : getText(language, txt.form.send.en, txt.form.send.fr, txt.form.send.ar)
                  }
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
